import React, {useEffect} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {useStoreActions} from '@/store';
import CellLoader from '@/components/loading/CellLoader';
import i18n from 'i18n';

/**
 * GeneralRedirectView
 *
 * Lightweight route component for `/app/insights/special/general`.
 * On mount it asks the backend for the best landing URL for the
 * "General" insights story and then navigates there client-side.
 */
const GeneralRedirectView: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const setSidebarActiveLinkRoute = useStoreActions(
        (actions) => actions.sidebarMenu.setActiveLinkRoute,
    );

    useEffect(() => {
        let isCancelled = false;

        const resolveAndNavigate = async () => {
            try {
                const response = await fetch('/internal_api/v1/system/general-landing-url', {
                    credentials: 'same-origin',
                    headers: {
                        'Accept': 'application/json',
                    },
                });

                if (!response.ok) {
                    throw new Error(`Failed to resolve general landing URL: ${response.status}`);
                }

                const data = await response.json();
                const targetUrl: string = data?.url || '/app/discover/my-stories';

                if (!isCancelled) {
                    // Preserve current query string, if any
                    const search = location.search || '';
                    // Keep sidebar highlighting in sync with the final destination,
                    // just like a normal sidebar click would do.
                    setSidebarActiveLinkRoute(targetUrl);
                    navigate(`${targetUrl}${search}`, {replace: true});
                }
            } catch (error) {
                console.error('GeneralRedirectView: error resolving landing URL, using fallback:', error);
                if (!isCancelled) {
                    const fallbackUrl = '/app/discover/my-stories';
                    setSidebarActiveLinkRoute(fallbackUrl);
                    navigate(fallbackUrl, {replace: true});
                }
            }
        };

        resolveAndNavigate();

        return () => {
            isCancelled = true;
        };
    }, [navigate, location.search]);

    return (
        <div style={{padding: '2rem', textAlign: 'center'}}>
            <h3 style={{marginBottom: '1rem', color: '#6c757d'}}>
                {i18n.t('redirecting_to_dashboard.text', 'Redirecting to dashboard...')}
            </h3>
            <CellLoader fullHeight={true} />
        </div>
    );
};

export default GeneralRedirectView;


